import {
  ArrowLeftRight,
  CalendarDays,
  Clock,
  GripVertical,
  History,
  Lock,
  MessageCircle,
  Move,
  MoreHorizontal,
  Pencil,
  Plus,
  Repeat,
  Send,
  Share2,
  Sparkles,
  Trash2,
  TriangleAlert,
  Undo2,
  X,
} from 'lucide-react';
import { type DragEvent, useMemo, useState } from 'react';

import MinistryIcon from '@/components/MinistryIcon';
import StatusBadge, { STATUS_META } from '@/components/StatusBadge';
import { cn } from '@/design-system/lib/cn';
import { IconTile } from '@/design-system/patterns/IconTile';
import { Avatar } from '@/design-system/ui/Avatar';
import { Button } from '@/design-system/ui/Button';
import { ProgressBar } from '@/design-system/ui/ProgressBar';
import { Sheet, SheetClose, SheetContent, SheetTitle } from '@/design-system/ui/Sheet';
import { Tabs, TabsContent } from '@/design-system/ui/Tabs';
import {
  PERIOD_LABEL,
  WEEKDAY_LONG,
  formatLongDate,
  monthKey,
  periodOf,
  relativeDay,
  relativeTime,
  weekdayOf,
} from '@escala/domain/dates';
import { shortName, paletteOf } from '@escala/domain/palette';
import { compareNames } from '@escala/domain/members';
import { leadersText } from '@escala/domain/permissions';
import {
  attendanceSummary,
  checkInOpen,
  declines,
  presenceOf,
  presenceSummary,
} from '@escala/domain/presence';
import { canNudgeAgain, canWhatsApp, reminderContext, whatsappMessage } from '@escala/domain/reminders';
import {
  type FunctionSlot,
  type MoveTarget,
  eventInfo,
  fillStateLabel,
  hasPositions,
  isScheduledIn,
  monthlyCounts,
  moveTargets,
  scopeInfo,
  suggestReplacement,
} from '@escala/domain/schedule';
import type { Attendance, Member, ScheduleEvent } from '@escala/domain/types';
import { useStore } from '@/store/AppStore';

import { type EventSheetTab, useOverlays } from '@/components/Overlays/context';

import {
  AttendanceBadge,
  CheckInPanel,
  CheckInToggle,
  DeclineRow,
  PresenceBar,
  ResponseBadge,
} from './Presence';
import AudiencePanel from './Audience';
import SheetTabs from './SheetTabs';
import EventSummary from './Summary';
import SwapRequests from './Swaps';
import WhatsAppDialog from './WhatsAppDialog';

type Drag = { memberId: string; fromRoleId: string };

function readOnlyText(
  permissions: { isManager: boolean; isAdmin: boolean; isRoleLeader: boolean },
  hasLocked: boolean,
  editableNames: string[]
): string {
  if (!permissions.isManager) return 'Você vê apenas os ministérios de que participa.';
  if (permissions.isAdmin || !hasLocked) return '';

  const fallback = permissions.isRoleLeader ? 'as funções que lidera' : 'os ministérios que lidera';

  return `Você edita apenas ${editableNames.join(', ') || fallback}. As demais aparecem somente para leitura.`;
}

function footNote(published: boolean, hasConflicts: boolean, generated: boolean): string {
  if (published) return 'Escala publicada. As alterações valem na hora e só quem mudou é avisado.';
  if (hasConflicts) return 'Resolva os conflitos antes de publicar.';

  return generated ? 'Rascunho: só a equipe vê esta escala.' : 'Escala ainda não montada.';
}

const AVAILABILITY_LABEL = {
  available: 'Disponível',
  unavailable: 'Indisponível',
  unset: 'Disponibilidade não configurada',
} as const;

function availabilityLabel(value: keyof typeof AVAILABILITY_LABEL, when: string): string {
  return value === 'unset' ? AVAILABILITY_LABEL.unset : `${AVAILABILITY_LABEL[value]} · ${when}`;
}

function subTone(hasIssue: boolean, value: keyof typeof AVAILABILITY_LABEL): string {
  if (hasIssue) return 'text-status-conflict';

  return value === 'available' ? 'text-status-complete' : 'text-ink-subtle';
}

/**
 * Painel lateral do evento: composição da escala por ministério e função, com edição
 * para quem pode (Admin; Líder de Ministério nos que lidera; Líder de Função nas funções
 * que lidera) e somente leitura para os
 * demais. O membro vê só os ministérios de que participa, sem proporções nem conflitos.
 */
export default function EventSheet({
  eventId,
  initialTab = 'escala',
  onClose,
}: {
  eventId: string;
  initialTab?: EventSheetTab;
  onClose: () => void;
}) {
  const { state, lookup, permissions, me, canUndo, actions, today } = useStore();
  const { openPicker, openShare, editEvent, deleteEvent } = useOverlays();
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [moveFor, setMoveFor] = useState<string | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  /** Rascunho do check-in (`null` = fora do modo de check-in). */
  const [checkIn, setCheckIn] = useState<Record<string, Attendance> | null>(null);
  /** Pendente sendo cobrado pelo WhatsApp (abre o diálogo com a mensagem pronta). */
  const [whatsFor, setWhatsFor] = useState<string | null>(null);
  // Momento da abertura do painel: a cobrança manual espera uma hora desde a anterior.
  const [openedAt] = useState(Date.now);
  const [tab, setTab] = useState<EventSheetTab>(initialTab);

  const event = state.events.find((e) => e.id === eventId);
  const counts = useMemo(
    () => (event ? monthlyCounts(state.events, monthKey(event.date)) : new Map()),
    [event, state.events]
  );
  const substitutes = useMemo(() => substitutesFor(event, state.members), [event, state.members]);

  if (!event) return null;

  const isManager = permissions.isManager;
  const myMinistries = Object.keys(me.roles);
  const full = eventInfo(event, lookup);
  // Membro: só os ministérios de que participa. A equipe vê tudo; cada líder edita só o seu.
  const info = isManager ? full : scopeInfo(full, myMinistries);
  // Sem posições (nenhum ministério ou todas as funções fora), o evento é só do calendário.
  const scheduled = hasPositions(full);
  const editable = (roleId: string) => permissions.editRole(roleId);
  const groupEditable = (group: (typeof info.groups)[number]) =>
    group.functions.some((f) => editable(f.role.id));
  const anyEditable = info.groups.some(groupEditable);
  const conflicts = info.conflicts;
  const log = state.log.filter((l) => l.eventId === event.id).slice(0, 12);
  const period = periodOf(event.time);
  const pct = info.needed ? Math.round((info.filled / info.needed) * 100) : 0;

  const roleKey = (roleId: string, memberId: string) => `${roleId}|${memberId}`;

  const targetsFor = (memberId: string, fromRoleId: string): MoveTarget[] =>
    moveTargets(event, memberId, fromRoleId, lookup).map((t) =>
      editable(t.roleId) ? t : { ...t, enabled: false, reason: 'Somente leitura' }
    );

  const dragTargets = drag ? targetsFor(drag.memberId, drag.fromRoleId) : [];

  const onDrop = (e: DragEvent, roleId: string) => {
    e.preventDefault();

    if (drag && dragTargets.some((t) => t.roleId === roleId && t.enabled)) {
      actions.move(event.id, drag.fromRoleId, roleId, drag.memberId);
    }

    setDrag(null);
  };

  const editableNames = permissions.isRoleLeader
    ? info.groups.flatMap((g) => g.functions.filter((f) => editable(f.role.id)).map((f) => f.role.name))
    : info.groups.filter(groupEditable).map((g) => g.ministry.name);
  // Presença: as contas e a cobrança só nas funções que o perfil edita (todas, no Administrador).
  const myRoleIds = permissions.isAdmin
    ? null
    : info.groups.flatMap((g) => g.functions.filter((f) => editable(f.role.id)).map((f) => f.role.id));
  const upcoming = event.date >= today;
  const showPresence = isManager && event.published && upcoming && info.people.length > 0;
  const canCheckIn = isManager && anyEditable && checkInOpen(event, today);
  const draftEvent: ScheduleEvent = checkIn
    ? { ...event, attendance: { ...event.attendance, ...checkIn } }
    : event;
  const inMyRoles = (roleId: string) => !myRoleIds || myRoleIds.includes(roleId);
  const myPeople = Object.entries(event.slots)
    .filter(([roleId]) => inMyRoles(roleId))
    .flatMap(([, ids]) => ids);
  const memberName = (id: string) => lookup.members.get(id)?.name ?? '';
  // O WhatsApp ao lado de "Pendente": só para quem edita a função e enquanto cabe cobrar.
  const whatsappable = (roleId: string, memberId: string) => {
    const member = lookup.members.get(memberId);

    return showPresence && editable(roleId) && !!member && canWhatsApp(event, member, today);
  };
  const whatsTarget = whatsFor ? reminderContext(event, whatsFor, lookup) : null;
  const whatsMember = whatsFor ? lookup.members.get(whatsFor) : undefined;

  const roText = readOnlyText(
    permissions,
    info.groups.some((g) => g.functions.some((f) => !editable(f.role.id))),
    editableNames
  );

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent aria-label={event.title}>
        <div className="flex flex-col gap-3 border-b border-line-divider px-5 py-4.5 max-md:gap-2.5 max-md:px-4 max-md:pt-[calc(0.75rem+env(safe-area-inset-top,0px))] max-md:pb-3.5">
          <div className="flex flex-col-reverse gap-2 md:flex-row md:items-start md:gap-3">
            <div className="min-w-0 flex-1 max-md:w-full">
              <p className="m-0 flex flex-wrap items-center gap-1.5 text-meta text-ink-subtle">
                {formatLongDate(event.date)} · {event.time}
                <span className="inline-flex items-center gap-1">
                  · <Repeat aria-hidden className="size-3.25 stroke-[1.8]" />
                  {event.recurrence ?? 'Evento único'}
                </span>
              </p>
              <SheetTitle className="mt-1 text-title font-bold tracking-[-0.01em]">{event.title}</SheetTitle>
            </div>
            <div className="-mr-1.5 flex shrink-0 items-center justify-end gap-1 max-md:-mt-1 max-md:w-full">
              {permissions.manageEvents ? (
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label="Editar evento"
                  title="Editar evento"
                  onClick={() => editEvent(event)}
                >
                  <Pencil aria-hidden />
                </Button>
              ) : null}
              {permissions.manageEvents ? (
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label="Excluir esta data"
                  title="Excluir esta data"
                  onClick={() => deleteEvent(event)}
                  className="text-status-conflict hover:bg-danger-soft"
                >
                  <Trash2 aria-hidden />
                </Button>
              ) : null}
              {isManager ? (
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label="Compartilhar escala"
                  title="Compartilhar escala"
                  onClick={() => openShare([event.id])}
                >
                  <Share2 aria-hidden />
                </Button>
              ) : null}
              <SheetClose asChild>
                <Button size="icon" variant="ghost" aria-label="Fechar">
                  <X aria-hidden />
                </Button>
              </SheetClose>
            </div>
          </div>
        </div>

        <Tabs
          value={isManager ? tab : 'escala'}
          onValueChange={(value) => setTab(value as EventSheetTab)}
          className="flex min-h-0 flex-1 flex-col"
        >
          {isManager ? <SheetTabs event={event} today={today} /> : null}

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            {isManager ? (
              <TabsContent value="publico">
                <AudiencePanel event={event} />
              </TabsContent>
            ) : null}
            {isManager ? (
              <TabsContent value="resumo">
                <EventSummary event={event} info={full} />
              </TabsContent>
            ) : null}
            <TabsContent value="escala">
              <div className="flex flex-col gap-4.5">
                {isManager && !scheduled ? (
                  <div className="flex gap-2.5 rounded-block border border-line-input bg-surface-subtle px-3.5 py-3 text-small text-ink-secondary">
                    <CalendarDays
                      aria-hidden
                      className="mt-0.5 size-4 shrink-0 stroke-[1.8] text-ink-muted"
                    />
                    <span>
                      Este evento não tem escala: nenhum ministério envolvido, então ele aparece só no
                      calendário. Para escalar pessoas, edite o evento e escolha os ministérios.
                    </span>
                  </div>
                ) : null}
                {isManager && scheduled ? (
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <StatusBadge status={info.status} />
                      <span className="rounded-chip bg-primary-soft px-2 py-0.5 text-caption font-medium text-primary-hover">
                        {relativeDay(event.date)}
                      </span>
                      <span className="text-small text-ink-muted">
                        {info.filled} de {info.needed} posições preenchidas
                      </span>
                    </div>
                    <ProgressBar
                      value={pct}
                      barClassName={STATUS_META[info.status].bar}
                      label="Posições preenchidas"
                    />
                  </div>
                ) : null}

                {showPresence ? (
                  <PresenceBar
                    summary={presenceSummary(event, myRoleIds)}
                    canNudge={anyEditable}
                    nudgeScope={myRoleIds ? 'Só as suas funções' : ''}
                    nudgedAt={event.nudgedAt}
                    cooling={!canNudgeAgain(event, openedAt)}
                    onNudge={() => actions.nudge(event.id)}
                  />
                ) : null}

                {canCheckIn ? (
                  <CheckInPanel
                    editing={!!checkIn}
                    summary={attendanceSummary(draftEvent, myRoleIds)}
                    scopeNote={myRoleIds ? 'O check-in vale para as funções que você edita.' : ''}
                    onStart={() =>
                      setCheckIn(
                        Object.fromEntries(
                          myPeople.flatMap((id) => (event.attendance[id] ? [[id, event.attendance[id]]] : []))
                        )
                      )
                    }
                    onAllPresent={() =>
                      setCheckIn((marks) => ({
                        ...marks,
                        ...Object.fromEntries(
                          myPeople
                            .filter((id) => marks?.[id]?.status !== 'absent')
                            .map((id) => [id, { status: 'present' as const, substituteId: null }])
                        ),
                      }))
                    }
                    onDone={() => {
                      if (checkIn) actions.recordAttendance(event.id, checkIn);
                      setCheckIn(null);
                    }}
                    onCancel={() => setCheckIn(null)}
                  />
                ) : null}

                {isManager && upcoming ? <SwapRequests event={event} /> : null}

                {isManager && conflicts.length ? (
                  <div
                    role="alert"
                    className="flex gap-2.5 rounded-block border border-status-conflict-line bg-danger-soft px-3.5 py-3"
                  >
                    <TriangleAlert
                      aria-hidden
                      className="mt-0.5 size-4 shrink-0 stroke-[1.8] text-status-conflict"
                    />
                    <div className="flex min-w-0 flex-col gap-1">
                      <p className="m-0 text-control font-bold text-danger-deep">
                        {conflicts.length === 1
                          ? '1 conflito nesta escala'
                          : `${conflicts.length} conflitos nesta escala`}
                      </p>
                      {conflicts.map((c) => {
                        const suggestion = editable(c.role.id)
                          ? suggestReplacement(event, c.role.id, c.member.id, lookup, counts)
                          : null;

                        return (
                          <div
                            key={roleKey(c.role.id, c.member.id)}
                            className="flex flex-col gap-1.5 border-t border-status-conflict-line pt-1.5"
                          >
                            <span className="text-small text-danger-ink">{c.issue.message}</span>
                            {suggestion ? (
                              <Button
                                size="xs"
                                variant="soft"
                                className="self-start"
                                title="Habilitado, disponível, sem ausência e o menos escalado no mês"
                                onClick={() =>
                                  actions.assign(event.id, c.role.id, suggestion.id, c.member.id)
                                }
                              >
                                <Sparkles aria-hidden />
                                Substituir por {shortName(suggestion.name)}
                              </Button>
                            ) : null}
                            {!suggestion && editable(c.role.id) ? (
                              <span className="text-meta text-danger-ink">
                                Nenhum substituto disponível. Use ⋯ › Substituir para ver todos os
                                habilitados.
                              </span>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                {isManager && scheduled && !event.generated ? (
                  <div className="flex gap-2.5 rounded-block border border-line-input bg-surface-subtle px-3.5 py-3 text-small text-ink-secondary">
                    <Clock aria-hidden className="mt-0.5 size-4 shrink-0 stroke-[1.8] text-ink-muted" />
                    Este evento ainda não tem escala gerada. Preencha manualmente ou use a geração automática.
                  </div>
                ) : null}

                {anyEditable && info.people.length ? (
                  <p className="m-0 flex items-center gap-1.5 text-meta text-ink-subtle">
                    <GripVertical aria-hidden className="size-4" />
                    Arraste um participante para outra função ou use o menu ⋯ › Mover.
                  </p>
                ) : null}

                {roText ? (
                  <p className="m-0 flex items-center gap-2 rounded-control bg-surface-muted px-3.5 py-2.5 text-small text-ink-muted">
                    <Lock aria-hidden className="size-4 shrink-0 stroke-[1.8]" />
                    {roText}
                  </p>
                ) : null}

                {info.groups.map((group) => {
                  const canEdit = groupEditable(group);
                  const leaders = leadersText(
                    group.ministry.leaderIds.flatMap((id) => lookup.members.get(id)?.name ?? [])
                  );
                  const gFilled = group.functions.reduce((a, f) => a + Math.min(f.people.length, f.qty), 0);
                  const gNeeded = group.functions.reduce((a, f) => a + f.qty, 0);

                  return (
                    <section
                      key={group.ministry.id}
                      aria-label={group.ministry.name}
                      className="shrink-0 rounded-block border border-line-card"
                    >
                      <div className="flex items-center gap-2.5 border-b border-line-divider px-3.5 py-3">
                        <IconTile size="sm" palette={paletteOf(group.ministry.color)}>
                          <MinistryIcon icon={group.ministry.icon} />
                        </IconTile>
                        <div className="min-w-0 flex-1">
                          <h3 className="m-0 text-body font-bold">{group.ministry.name}</h3>
                          <p className="m-0 text-caption text-ink-subtle">
                            {isManager ? `${gFilled}/${gNeeded} preenchidas · ` : ''}
                            {leaders}
                          </p>
                        </div>
                        {isManager && !canEdit ? (
                          <span className="inline-flex items-center gap-1 text-caption text-ink-subtle">
                            <Lock aria-hidden className="size-3.5" />
                            Somente leitura
                          </span>
                        ) : null}
                      </div>
                      <div className="flex flex-col gap-3 px-3.5 py-3">
                        {group.functions.map((fn) => (
                          <FunctionBlock
                            key={fn.role.id}
                            fn={fn}
                            eventId={event.id}
                            isManager={isManager}
                            canEdit={editable(fn.role.id)}
                            weekday={WEEKDAY_LONG[weekdayOf(event.date)]!.toLowerCase()}
                            periodLabel={PERIOD_LABEL[period].toLowerCase()}
                            drag={drag}
                            dropTarget={drag ? dragTargets.find((t) => t.roleId === fn.role.id) : undefined}
                            onDragStart={(memberId) => setDrag({ memberId, fromRoleId: fn.role.id })}
                            onDragEnd={() => setDrag(null)}
                            onDrop={(e) => onDrop(e, fn.role.id)}
                            menuFor={menuFor}
                            moveFor={moveFor}
                            onToggleMenu={(key) => {
                              setMenuFor(menuFor === key ? null : key);
                              setMoveFor(null);
                            }}
                            onToggleMove={(key) => setMoveFor(moveFor === key ? null : key)}
                            targetsFor={(memberId) => targetsFor(memberId, fn.role.id)}
                            onReplace={(memberId) => {
                              setMenuFor(null);
                              openPicker({ eventId: event.id, roleId: fn.role.id, replaceId: memberId });
                            }}
                            onMove={(memberId, toRoleId) => {
                              setMenuFor(null);
                              actions.move(event.id, fn.role.id, toRoleId, memberId);
                            }}
                            onRemove={(memberId) => {
                              setMenuFor(null);
                              actions.remove(event.id, fn.role.id, memberId);
                            }}
                            onAdd={() =>
                              openPicker({ eventId: event.id, roleId: fn.role.id, replaceId: null })
                            }
                            response={(memberId) =>
                              showPresence
                                ? {
                                    state: presenceOf(event, memberId),
                                    at: event.responses[memberId]?.at ?? null,
                                  }
                                : null
                            }
                            attendance={(memberId) => {
                              const mark = draftEvent.attendance[memberId];

                              return mark
                                ? { mark, substitute: mark.substituteId ? memberName(mark.substituteId) : '' }
                                : null;
                            }}
                            whatsapp={(memberId) => whatsappable(fn.role.id, memberId)}
                            onWhatsApp={setWhatsFor}
                            checkingIn={!!checkIn && editable(fn.role.id)}
                            substitutes={substitutes}
                            onMark={(memberId, mark) =>
                              setCheckIn((marks) => ({ ...marks, [memberId]: mark }))
                            }
                            declines={
                              isManager && upcoming
                                ? declines(event, [fn.role.id]).map((d) => ({
                                    decline: d,
                                    name: memberName(d.memberId),
                                    suggestion: editable(fn.role.id)
                                      ? suggestReplacement(event, fn.role.id, d.memberId, lookup, counts)
                                      : null,
                                  }))
                                : []
                            }
                            onFill={(memberId) => actions.assign(event.id, fn.role.id, memberId)}
                          />
                        ))}
                      </div>
                    </section>
                  );
                })}

                {isManager ? (
                  <section
                    aria-label="Histórico de alterações"
                    className="flex shrink-0 flex-col gap-2.5 border-t border-line-divider pt-3.5"
                  >
                    <div className="flex items-center gap-2">
                      <History aria-hidden className="size-4 stroke-[1.8] text-ink-subtle" />
                      <h3 className="m-0 text-control font-bold">Histórico de alterações</h3>
                    </div>
                    {log.length ? (
                      log.map((entry) => (
                        <div key={entry.id} className="flex items-start gap-2.5">
                          <Avatar name={entry.who} size="xs" />
                          <div className="min-w-0 flex-1 text-small leading-[1.35]">
                            <span className="font-semibold">{entry.who}</span> ·{' '}
                            <span className="text-ink-secondary">{entry.text}</span>
                            <div className="text-caption text-ink-subtle">{relativeTime(entry.at)}</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="m-0 text-small text-ink-subtle">Nenhuma alteração registrada.</p>
                    )}
                  </section>
                ) : null}
              </div>
            </TabsContent>
          </div>

          {isManager && scheduled && tab === 'escala' ? (
            <footer className="flex flex-col gap-2.5 border-t border-line-divider px-5 py-3 max-md:px-4 max-md:pt-3 max-md:pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] md:flex-row md:items-center">
              <p className="m-0 text-meta text-ink-subtle md:order-2 md:min-w-30 md:flex-1">
                {footNote(event.published, conflicts.length > 0, event.generated)}
              </p>
              {anyEditable ? (
                <div className="flex w-full items-center justify-between gap-2.5 md:contents">
                  <Button
                    variant="secondary"
                    onClick={actions.undo}
                    disabled={!canUndo}
                    className="md:order-1"
                  >
                    <Undo2 aria-hidden />
                    Desfazer
                  </Button>
                  <Button
                    onClick={() => actions.publish(event.id)}
                    disabled={event.published || conflicts.length > 0 || !info.people.length}
                    className="md:order-3"
                  >
                    <Send aria-hidden />
                    {event.published ? 'Publicada' : 'Publicar escala'}
                  </Button>
                </div>
              ) : null}
            </footer>
          ) : null}
        </Tabs>
      </SheetContent>
      {whatsTarget && whatsMember ? (
        <WhatsAppDialog
          name={whatsMember.name}
          photo={whatsMember.photo}
          phone={whatsMember.phone}
          assignment={`${whatsTarget.ministryName} · ${whatsTarget.roleName}`}
          event={event}
          message={whatsappMessage(whatsTarget, me.name)}
          onOpen={() => {
            actions.whatsapp(event.id, whatsMember.id);
            setWhatsFor(null);
          }}
          onClose={() => setWhatsFor(null)}
        />
      ) : null}
    </Sheet>
  );
}

type FunctionBlockProps = {
  fn: FunctionSlot;
  eventId: string;
  isManager: boolean;
  canEdit: boolean;
  weekday: string;
  periodLabel: string;
  drag: Drag | null;
  dropTarget: MoveTarget | undefined;
  onDragStart: (memberId: string) => void;
  onDragEnd: () => void;
  onDrop: (e: DragEvent) => void;
  menuFor: string | null;
  moveFor: string | null;
  onToggleMenu: (key: string) => void;
  onToggleMove: (key: string) => void;
  targetsFor: (memberId: string) => MoveTarget[];
  onReplace: (memberId: string) => void;
  onMove: (memberId: string, toRoleId: string) => void;
  onRemove: (memberId: string) => void;
  onAdd: () => void;
  response: (memberId: string) => { state: ReturnType<typeof presenceOf>; at: number | null } | null;
  attendance: (memberId: string) => { mark: Attendance; substitute: string } | null;
  /** Mostra o botão do WhatsApp ao lado de "Pendente". */
  whatsapp: (memberId: string) => boolean;
  onWhatsApp: (memberId: string) => void;
  checkingIn: boolean;
  substitutes: readonly Member[];
  onMark: (memberId: string, mark: Attendance) => void;
  declines: { decline: ReturnType<typeof declines>[number]; name: string; suggestion: Member | null }[];
  onFill: (memberId: string) => void;
};

function FunctionBlock({
  fn,
  isManager,
  canEdit,
  weekday,
  periodLabel,
  drag,
  dropTarget,
  onDragStart,
  onDragEnd,
  onDrop,
  menuFor,
  moveFor,
  onToggleMenu,
  onToggleMove,
  targetsFor,
  onReplace,
  onMove,
  onRemove,
  onAdd,
  response,
  attendance,
  whatsapp,
  onWhatsApp,
  checkingIn,
  substitutes,
  onMark,
  declines: declined,
  onFill,
}: FunctionBlockProps) {
  const have = fn.people.length;
  const isSource = drag?.fromRoleId === fn.role.id;
  const isTarget = !!drag && !isSource;
  const valid = isTarget && !!dropTarget?.enabled;

  return (
    <div
      onDragOver={(e) => {
        if (valid) e.preventDefault();
      }}
      onDrop={onDrop}
      className={cn(
        'overflow-hidden rounded-control transition-colors',
        valid
          ? 'border-2 border-dashed border-primary-focus bg-primary-faint'
          : 'border border-line-card bg-white'
      )}
    >
      <div className="flex items-center justify-between gap-2 bg-surface-subtle px-3 py-2">
        <span className="text-control font-semibold">
          {fn.role.name}
          {isManager ? (
            <>
              {' '}
              <span className="text-ink-placeholder">·</span>{' '}
              <span data-numeric>
                {have}/{fn.qty}
              </span>
            </>
          ) : null}
        </span>
        {isManager ? (
          <span
            className={cn(
              'text-caption font-semibold',
              have < fn.qty ? 'text-status-partial' : 'text-ink-subtle'
            )}
          >
            {fillStateLabel(have, fn.qty)}
          </span>
        ) : null}
      </div>
      {isTarget ? (
        <div
          className={cn(
            'border-t border-line-divider px-3 py-1.5 text-caption font-medium',
            valid ? 'bg-primary-soft text-primary-hover' : 'bg-surface-subtle text-ink-subtle'
          )}
        >
          {valid
            ? dropTarget?.reason || 'Soltar aqui'
            : `Não é possível mover: ${dropTarget?.reason ?? 'função de outro evento'}`}
        </div>
      ) : null}

      {fn.people.map((person) => {
        const key = `${fn.role.id}|${person.member.id}`;
        const menuOpen = menuFor === key;
        const moveOpen = moveFor === key;
        const availabilityText = availabilityLabel(person.availability, `${weekday} ${periodLabel}`);

        return (
          <div key={person.member.id} className="border-t border-line-divider">
            <div
              draggable={canEdit}
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move';
                onDragStart(person.member.id);
              }}
              onDragEnd={onDragEnd}
              className={cn(
                'flex items-center gap-2.5 px-3 py-2',
                canEdit && 'cursor-grab',
                drag?.memberId === person.member.id && isSource && 'opacity-50'
              )}
            >
              {canEdit ? (
                <GripVertical aria-hidden className="-mr-1 -ml-1.5 size-4 text-line-dashed" />
              ) : null}
              <Avatar name={person.member.name} photo={person.member.photo} size="lg" className="size-8.5" />
              {/* Nome e selos quebram em duas linhas quando não cabem lado a lado (celular). */}
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2.5 gap-y-1.5">
                <div className="min-w-0 grow basis-40">
                  <div className="flex items-center gap-1.5 text-control font-semibold">
                    {person.member.name}
                    {person.extra ? (
                      <span
                        title="Acima da quantidade padrão da função"
                        className="rounded-chip bg-surface-muted px-1.75 py-px text-micro font-semibold text-ink-muted"
                      >
                        Extra
                      </span>
                    ) : null}
                  </div>
                  {isManager ? (
                    <div
                      className={cn(
                        'flex items-center gap-1 text-caption',
                        subTone(!!person.issue, person.availability)
                      )}
                    >
                      {person.issue ? <TriangleAlert aria-hidden className="size-3" /> : null}
                      {person.issue ? person.issue.label : availabilityText}
                    </div>
                  ) : null}
                </div>
                {checkingIn ? (
                  <CheckInToggle
                    name={person.member.name}
                    mark={attendance(person.member.id)?.mark}
                    substitutes={substitutes}
                    onChange={(mark) => onMark(person.member.id, mark)}
                  />
                ) : null}
                {!checkingIn && attendance(person.member.id) ? (
                  <AttendanceBadge
                    mark={attendance(person.member.id)!.mark}
                    substitute={attendance(person.member.id)!.substitute}
                  />
                ) : null}
                {!checkingIn && !attendance(person.member.id) && whatsapp(person.member.id) ? (
                  <button
                    type="button"
                    aria-label={`Cobrar ${person.member.name} pelo WhatsApp`}
                    title="Cobrar confirmação pelo WhatsApp"
                    onClick={() => onWhatsApp(person.member.id)}
                    className="grid size-6.5 shrink-0 place-items-center rounded-pill border border-status-complete-line bg-success-soft text-success hover:bg-status-complete-bg"
                  >
                    <MessageCircle aria-hidden className="size-3.5 stroke-2" />
                  </button>
                ) : null}
                {!checkingIn && !attendance(person.member.id) && response(person.member.id) ? (
                  <ResponseBadge
                    state={response(person.member.id)!.state}
                    at={response(person.member.id)!.at}
                  />
                ) : null}
              </div>
              {canEdit && !checkingIn ? (
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label={`Ações para ${person.member.name}`}
                  aria-expanded={menuOpen}
                  onClick={() => onToggleMenu(key)}
                >
                  <MoreHorizontal aria-hidden />
                </Button>
              ) : null}
            </div>
            {menuOpen ? (
              <div className="flex flex-col gap-2 pr-3 pb-3 pl-14">
                <div className="flex flex-wrap gap-1.5">
                  <Button size="xs" variant="secondary" onClick={() => onReplace(person.member.id)}>
                    <ArrowLeftRight aria-hidden />
                    Substituir
                  </Button>
                  <Button
                    size="xs"
                    variant="secondary"
                    aria-expanded={moveOpen}
                    onClick={() => onToggleMove(key)}
                  >
                    <Move aria-hidden />
                    Mover
                  </Button>
                  <Button size="xs" variant="destructive" onClick={() => onRemove(person.member.id)}>
                    <Trash2 aria-hidden />
                    Remover
                  </Button>
                </div>
                {moveOpen ? (
                  <div className="flex flex-col rounded-control border border-line-input p-1">
                    <span className="px-2 py-1 text-caption text-ink-subtle">Mover para</span>
                    {targetsFor(person.member.id).map((t) => (
                      <button
                        key={t.roleId}
                        type="button"
                        disabled={!t.enabled}
                        onClick={() => onMove(person.member.id, t.roleId)}
                        className={cn(
                          'flex min-h-9 items-center justify-between gap-2 rounded-chip px-2 text-left text-small',
                          t.enabled ? 'text-ink hover:bg-surface-muted' : 'text-ink-placeholder'
                        )}
                      >
                        <span>{t.label}</span>
                        <span className="text-caption text-ink-subtle">{t.reason}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}

      {declined.map((d) => (
        <DeclineRow
          key={d.decline.memberId}
          decline={d.decline}
          name={d.name}
          canEdit={canEdit}
          suggestion={d.suggestion}
          onFill={onFill}
          onPick={onAdd}
        />
      ))}

      {isManager
        ? Array.from({ length: fn.open }, (_, i) =>
            canEdit ? (
              <button
                key={i}
                type="button"
                onClick={onAdd}
                className="flex w-full items-center gap-2.5 border-t border-line-divider bg-white px-3 py-2 text-left text-small font-semibold text-primary hover:bg-primary-faint"
              >
                <span className="grid size-8.5 place-items-center rounded-pill border-[1.5px] border-dashed border-primary-hover-line">
                  <Plus aria-hidden className="size-3.5 stroke-2" />
                </span>
                Adicionar membro
              </button>
            ) : (
              <div
                key={i}
                className="flex items-center gap-2.5 border-t border-line-divider px-3 py-2 text-small text-ink-subtle"
              >
                <span
                  aria-hidden
                  className="size-8.5 rounded-pill border-[1.5px] border-dashed border-line-dashed"
                />
                Vaga em aberto
              </div>
            )
          )
        : null}

      {canEdit && fn.open === 0 ? (
        <button
          type="button"
          onClick={onAdd}
          aria-label={`Adicionar extra em ${fn.role.name}`}
          title={`Adicionar extra em ${fn.role.name}`}
          className="flex w-full items-center gap-2 border-t border-line-divider bg-white px-3 py-1.75 text-left text-meta font-medium text-ink-subtle hover:bg-surface-subtle hover:text-primary-hover"
        >
          <Plus aria-hidden className="size-4 stroke-[1.8]" />
          Adicionar extra
        </button>
      ) : null}
    </div>
  );
}

/** Para abrir o painel só nos eventos que o perfil pode ver (o membro, só onde serve). */
export function canOpenEvent(
  isManager: boolean,
  event: Parameters<typeof isScheduledIn>[0],
  memberId: string
): boolean {
  return isManager || isScheduledIn(event, memberId);
}

/** Quem pode ter servido no lugar de alguém: os membros fora da escala do evento. */
function substitutesFor(event: ScheduleEvent | undefined, members: readonly Member[]): Member[] {
  if (!event) return [];

  const inEvent = new Set(Object.values(event.slots).flat());

  // Serviu no lugar de alguém neste dia: quem já estava inativo na data não entra.
  return members
    .filter((m) => !inEvent.has(m.id) && (!m.inactiveSince || event.date < m.inactiveSince))
    .sort((a, b) => compareNames(a.name, b.name));
}
